/**
 * Document upload and management service
 */
import { api } from './api';

export interface SubmissionDocument {
  id: number;
  submission: number;
  document_type: string;
  document_type_display: string;
  filename: string;
  file: string;
  file_size: number;
  s3_key: string;
  processing_status: 'uploading' | 'processing' | 'ready' | 'error';
  processing_status_display: string;
  processing_error: string;
  ai_analysis: Record<string, any>;
  tags: string[];
  uploaded_by: number;
  uploaded_by_name: string;
  uploaded_at: string;
  text_extraction_completed_at: string | null;
  ai_analysis_completed_at: string | null;
}

export interface DocumentStatus {
  id: number;
  filename: string;
  processing_status: 'uploading' | 'processing' | 'ready' | 'error';
  processing_error: string;
  text_extraction_completed: boolean;
  ai_analysis_completed: boolean;
  ai_analysis: Record<string, any> | null;
}

export class DocumentService {
  /**
   * Upload a document file
   */
  async uploadDocument(
    file: File,
    submissionId: number,
    documentType?: string
  ): Promise<SubmissionDocument> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('submission_id', submissionId.toString());
    if (documentType) {
      formData.append('document_type', documentType);
    }

    return api.post<SubmissionDocument>('/api/submission-documents/upload/', formData);
  }

  /**
   * Get document processing status
   */
  async getDocumentStatus(documentId: number): Promise<DocumentStatus> {
    return api.get<DocumentStatus>(`/api/submission-documents/${documentId}/status/`);
  }

  /**
   * List documents for a submission
   */
  async listDocuments(submissionId: number): Promise<SubmissionDocument[]> {
    return api.get<SubmissionDocument[]>(`/api/submission-documents/?submission=${submissionId}`);
  }

  /**
   * Delete a document
   */
  async deleteDocument(documentId: number): Promise<void> {
    return api.delete(`/api/submission-documents/${documentId}/`);
  }

  /**
   * Poll document status until processing is complete
   */
  async pollDocumentStatus(
    documentId: number,
    onUpdate: (status: DocumentStatus) => void,
    intervalMs: number = 2000,
    maxAttempts: number = 60
  ): Promise<DocumentStatus> {
    let attempts = 0;

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const status = await this.getDocumentStatus(documentId);
          onUpdate(status);

          if (status.processing_status === 'ready' || status.processing_status === 'error') {
            resolve(status);
            return;
          }

          attempts++;
          if (attempts >= maxAttempts) {
            reject(new Error('Polling timeout'));
            return;
          }

          setTimeout(poll, intervalMs);
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  }
}

export const documentService = new DocumentService();

